import {
  useContext, useEffect, useRef, useState,
} from 'react';
import SEOSetting from '../../Blocks/Core/Modals/SEOSetting';
import GoogleAnalyticsSetting from '../../Blocks/Core/Modals/GoogleAnalyticsSetting';
import { PagesContext, PageSetting } from '../../../contexts/PagesContext';
import PageSettingModal from '../../Blocks/Core/Modals/PageSetting';
import BasicSetting from '../../Blocks/Core/Modals/BasicSettings';
import ModalComponent from '../../ModalComponent';
import GoogleSearchConsoleSetting from '../../Blocks/Core/Modals/GoogleSearchConsoleSetting';
import AnimationSetting from '../../Blocks/Core/Modals/AnimationSetting';
import ChangeLayoutSetting from '../../Blocks/Core/Modals/ChangeLayoutSetting';
import PasswordSetting from '../../Blocks/Core/Modals/PasswordSetting';

interface Props {
  pageId: string | null;
  open: boolean;
  loading: boolean;
  settings: PageSetting;
  handleClose: () => void;
  handleSavePageSetting: (settings: PageSetting) => void;
}

const Index = (props: Props) => {
  const {
    pageId,
    open,
    settings,
    loading,
    handleClose,
    handleSavePageSetting,
  } = props;

  const [activeTab, setActiveTab] = useState<string>('BasicSettings');
  const [pageSettings, setPageSettings] = useState<PageSetting>(settings);

  const [isSwitchOn, setIsSwitchOn] = useState(!!pageSettings.password);
  const [disableSaveButton, setDisableSaveButton] = useState(false);

  const { contextErrors } = useContext(PagesContext);

  const defaultMainColor = useRef(settings?.main_color);
  const defaultTextColor = useRef(settings?.text_color);
  const defaultBackgroundColor = useRef(settings?.background_color);
  const defaultTextFont = useRef(settings?.text_font);

  const onClose = () => {
    setPageSettings((prevSettings) => ({
      ...prevSettings,
      main_color: defaultMainColor.current,
      text_color: defaultTextColor.current,
      background_color: defaultBackgroundColor.current,
      text_font: defaultTextFont.current,
    }));
    handleClose();
  };

  const handleSave = () => {
    handleSavePageSetting({
      title: pageSettings.title,
      description: pageSettings.description,
      keywords: pageSettings.keywords,
      favicon_file: pageSettings.favicon_file,
      header_script: pageSettings.header_script,
      body_script: pageSettings.body_script,
      smart_phone_width: pageSettings.smart_phone_width,
      desk_top_width: pageSettings.desk_top_width,
      google_analytics_script: pageSettings.google_analytics_script,
      is_robots_noindex: pageSettings.is_robots_noindex,
      url: pageSettings.url,
      animation: pageSettings.animation,
      google_search_console_url: pageSettings.google_search_console_url,
      background_color: pageSettings.background_color,
      text_font: pageSettings.text_font,
      main_color: pageSettings.main_color,
      text_color: pageSettings.text_color,
      password: pageSettings.password,
      auth_page_description: pageSettings.auth_page_description,
      should_deactivate_password_protection: !isSwitchOn,
    });

    defaultMainColor.current = pageSettings.main_color;
    defaultTextColor.current = pageSettings.text_color;
    defaultBackgroundColor.current = pageSettings.background_color;
    defaultTextFont.current = pageSettings.text_font;
  };

  const handleSaveGoogleSearchConsoleUrl = (url: string) => {
    handleSavePageSetting({
      google_search_console_url: url,
    });
  };

  useEffect(() => {
    if (contextErrors?.password || contextErrors?.auth_page_description) {
      setActiveTab('PasswordSettings');
    }
  }, [contextErrors?.password]);

  return (
    <ModalComponent
      size="xl"
      title="ページ設定"
      confirmButtonText="保存する"
      cancelButtonText="閉じる"
      onConfirm={handleSave}
      loading={loading}
      show={open}
      closeModal={onClose}
      disabled={disableSaveButton}
      centered
      scrollable
      animation={false}
      bodyClassName="m-0 p-0"
      contentClassName="fullViewportHeight"
      style={{
        zIndex: 1050,
      }}
    >
      <div className="tab modalTabs">
        <ul className="nav navTabs">
          <li>
            <a
              className={activeTab === 'BasicSettings' ? 'active' : ''}
              href="#"
              onClick={() => setActiveTab('BasicSettings')}
            >
              基本設定
            </a>
          </li>
          <li>
            <a
              className={activeTab === 'SEOSettings' ? 'active' : ''}
              href="#"
              onClick={() => setActiveTab('SEOSettings')}
            >
              タグの埋め込み・コンテンツサイズ
            </a>
          </li>
          <li>
            <a
              className={activeTab === 'GoogleAnalytics' ? 'active' : ''}
              href="#"
              onClick={() => setActiveTab('GoogleAnalytics')}
            >
              Google Analytics関連
            </a>
          </li>

          <li>
            <a
              className={activeTab === 'AnimationSettings' ? 'active' : ''}
              href="#"
              onClick={() => setActiveTab('AnimationSettings')}
            >
              アニメーション設定
            </a>
          </li>
          <li>
            <a
              className={activeTab === 'ChangeLayoutSetting' ? 'active' : ''}
              href="#"
              onClick={() => setActiveTab('ChangeLayoutSetting')}
            >
              カラー変更・フォント変更
            </a>
          </li>
          <li>
            <a
              className={activeTab === 'GoogleSearchConsole' ? 'active' : ''}
              href="#"
              onClick={() => setActiveTab('GoogleSearchConsole')}
            >
              サーチコンソール連携
            </a>
          </li>
          <li>
            <a
              className={activeTab === 'PasswordSettings' ? 'active' : ''}
              href="#"
              onClick={() => setActiveTab('PasswordSettings')}
            >
              パスワード保護
            </a>
          </li>
        </ul>
        <div className="tab-content tabContent">
          {contextErrors && Object.keys(contextErrors).length > 0 && (
            <div className="alert alert-danger" style={{ margin: '20px' }}>
              <ul>
                {Object.entries(contextErrors).map(([key, error]) => (
                  <li key={key}>
                    {String(error)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div id="BasicSettings" className={`tab-pane tabPane ${activeTab === 'BasicSettings' ? 'active show' : ''}`}>
            <BasicSetting
              title={pageSettings?.title}
              description={pageSettings?.description}
              keywords={pageSettings?.keywords}
              faviconUrl={pageSettings?.favicon_url}
              faviconFile={pageSettings?.favicon_file}
              onTitleChange={(newTitle) => setPageSettings((prev) => ({ ...prev, title: newTitle }))}
              onDescriptionChange={(newDescription) => setPageSettings((prev) => ({ ...prev, description: newDescription }))}
              onKeywordsChange={(newKeywords) => setPageSettings((prev) => ({ ...prev, keywords: newKeywords }))}
              onFaviconFileChange={(newFaviconFile) => setPageSettings((prev) => ({ ...prev, favicon_file: newFaviconFile }))}
            />
          </div>
          <div id="SEOSettings" className={`tab-pane tabPane ${activeTab === 'SEOSettings' ? 'active show' : ''}`}>
            <h2 className="mb-2">タグの埋め込み・コンテンツサイズ設定</h2>
            <hr className="mt-4" />
            <div className="pb-4">
              <SEOSetting
                headerScript={pageSettings?.header_script}
                bodyScript={pageSettings?.body_script}
                onHeaderScriptChange={(newScript) => setPageSettings((prev) => ({ ...prev, header_script: newScript }))}
                onBodyScriptChange={(newScript) => setPageSettings((prev) => ({ ...prev, body_script: newScript }))}
              />
              <hr className="mt-4" />
              <PageSettingModal
                smartPhoneWidth={pageSettings?.smart_phone_width}
                deskTopWidth={pageSettings?.desk_top_width}
                onSmartPhoneWidthChange={(newWidth) => setPageSettings((prev) => ({ ...prev, smart_phone_width: newWidth }))}
                onDeskTopWidthChange={(newWidth) => setPageSettings((prev) => ({ ...prev, desk_top_width: newWidth }))}
              />
            </div>
          </div>
          <div id="GoogleAnalytics" className={`tab-pane tabPane ${activeTab === 'GoogleAnalytics' ? 'active show' : ''}`}>
            <GoogleAnalyticsSetting
              googleAnalyticsScript={pageSettings?.google_analytics_script}
              isRobotsNoindex={!!pageSettings?.is_robots_noindex}
              onGoogleAnalyticsScriptChange={(newScript) => setPageSettings((prev) => ({ ...prev, google_analytics_script: newScript }))}
              onIsRobotsNoindexChange={(newValue) => setPageSettings((prev) => ({ ...prev, is_robots_noindex: newValue }))}
            />
          </div>
          <div id="AnimationSettings" className={`tab-pane tabPane ${activeTab === 'AnimationSettings' ? 'active show' : ''}`}>
            <AnimationSetting animation={pageSettings?.animation} onAnimationChange={(animation) => setPageSettings((prev) => ({ ...prev, animation }))} />
          </div>
          <div id="ChangeLayoutSetting" className={`tab-pane tabPane ${activeTab === 'ChangeLayoutSetting' ? 'active show' : ''}`}>
            <ChangeLayoutSetting
              text_font={pageSettings?.text_font}
              main_color={pageSettings?.main_color}
              text_color={pageSettings?.text_color}
              background_color={pageSettings?.background_color}
              onMainColorChange={(main_color) => setPageSettings((prev) => ({ ...prev, main_color }))}
              onTextColorChange={(text_color) => setPageSettings((prev) => ({ ...prev, text_color }))}
              onBackgroundColorChange={(background_color) => setPageSettings((prev) => ({ ...prev, background_color }))}
              onFontChange={(text_font) => setPageSettings((prev) => ({ ...prev, text_font }))}
            />
          </div>
          <div id="GoogleSearchConsole" className={`tab-pane tabPane ${activeTab === 'GoogleSearchConsole' ? 'active show' : ''}`}>
            <GoogleSearchConsoleSetting
              title={pageSettings?.title}
              url={(pageSettings?.custom_url === '' || pageSettings?.custom_url == null) ? pageSettings?.url : pageSettings?.custom_url}
              pageId={pageId}
              settings={pageSettings}
              handleGoogleSearchConsoleUrlChange={
                (newUrl) => {
                  setPageSettings((prev) => ({ ...prev, google_search_console_url: newUrl }));
                  handleSaveGoogleSearchConsoleUrl(newUrl);
                }
              }
            />
          </div>
          <div id="PasswordSettings" className={`tab-pane tabPane ${activeTab === 'PasswordSettings' ? 'active show' : ''}`}>
            <PasswordSetting
              onPasswordChange={(newPassword: string) => setPageSettings((prev) => ({ ...prev, password: newPassword }))}
              onDescriptionTextChange={(newDescription: string) => setPageSettings((prev) => ({ ...prev, auth_page_description: newDescription }))}
              pageSettings={pageSettings}
              setIsSwitchOn={setIsSwitchOn}
              isSwitchOn={isSwitchOn}
              toggleSaveButton={(newValue: boolean) => setDisableSaveButton(newValue)}
            />
          </div>
        </div>
      </div>
    </ModalComponent>
  );
};

export default Index;
